import React, { useEffect, useRef, useState, useCallback } from "react";
import { TablePagination } from "@mui/material";
import { ApiService, DistributionListService } from "../../../../api";
import { SearchInput, CustomDialog, icon } from "../../../../common/components";
import { translate } from "../../../../common/providers";
import DistributionListTable from "./DistributionListTable";
import DistributionListSidePanel from "./DistributionListSidePanel";
import styles from "./DistributionList.module.css";

const rowsPerPageOptions = [25, 50, 75];

export default function DistributionList() {
	const [territories, setTerritories] = useState([]);
	const [totalElements, setTotalElements] = useState(0);
	const [search, setSearch] = useState("");

	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);

	const [sidePanelOpen, setSidePanelOpen] = useState(false);
	const [selectedTerritory, setSelectedTerritory] = useState(null);

	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [territoryToDelete, setTerritoryToDelete] = useState(null);

	const [isSorted, setIsSorted] = useState(false);
	const [sortDirection, setSortDirection] = useState("asc");

	const cancelTokenSourceRef = useRef(null);

	useEffect(() => {
		cancelTokenSourceRef.current = ApiService.getCancelTokenSource();
		return () => {
			ApiService.cancelTokens(cancelTokenSourceRef.current);
		};
	}, []);

	const fetchTerritories = useCallback(
		(currentPage, currentRowsPerPage, currentSortDirection) => {
			DistributionListService.getTerritoriesWithDistribution(
				{
					buName: search || undefined,
					page: currentPage,
					limit: currentRowsPerPage,
					sort: "name",
					direction: currentSortDirection.toUpperCase(),
				},
				cancelTokenSourceRef.current?.token
			)
				.then((response) => {
					setTerritories(response.contents || []);
					setTotalElements(response.totalElements || 0);
				})
				.catch((err) => {
					console.error("Error fetching territories:", err);
				});
		},
		[search]
	);

	useEffect(() => {
		if (cancelTokenSourceRef.current) {
			setPage(0);
			fetchTerritories(0, rowsPerPage, sortDirection);
		}
	}, [search, fetchTerritories, rowsPerPage, sortDirection]);

	const handleChangeSearch = (e) => {
		setSearch(e.target.value);
	};

	const handleClearSearch = () => {
		setSearch("");
	};

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
		fetchTerritories(newPage, rowsPerPage, sortDirection);
	};

	const handleChangeRowsPerPage = (event) => {
		const newRowsPerPage = parseInt(event.target.value, 10);
		setRowsPerPage(newRowsPerPage);
		setPage(0);
		fetchTerritories(0, newRowsPerPage, sortDirection);
	};

	const handleSort = () => {
		setIsSorted(true);
		setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
	};

	const handleOpenSidePanel = (territory) => {
		setSelectedTerritory(territory);
		setSidePanelOpen(true);
	};

	const handleCloseSidePanel = () => {
		setSelectedTerritory(null);
		setSidePanelOpen(false);
	};

	const handleSave = (territoryId, email) => {
		DistributionListService.createOrUpdate({ territoryId, email }, cancelTokenSourceRef.current?.token)
			.then(() => {
				fetchTerritories(page, rowsPerPage, sortDirection);
				handleCloseSidePanel();
			})
			.catch(console.error);
	};

	const handleOpenDeleteDialog = (territory) => {
		setTerritoryToDelete(territory);
		setDeleteDialogOpen(true);
	};

	const handleCloseDeleteDialog = () => {
		setTerritoryToDelete(null);
		setDeleteDialogOpen(false);
	};

	const handleConfirmDelete = () => {
		if (territoryToDelete) {
			DistributionListService.deleteByTerritoryId(territoryToDelete.id, cancelTokenSourceRef.current?.token)
				.then(() => {
					fetchTerritories(page, rowsPerPage, sortDirection);
					handleCloseDeleteDialog();
				})
				.catch((err) => {
					console.error("Error deleting distribution list:", err);
				});
		}
	};

	return (
		<>
			<div className={styles.header}>
				<SearchInput
					className={styles.searchInput}
					label={translate("admin.distribution-list.search.placeholder")}
					value={search}
					onChange={handleChangeSearch}
					onClearSearch={handleClearSearch}
				/>
			</div>
			<div className={styles.tableContainer}>
				<DistributionListTable
					isSorted={isSorted}
					sortDirection={sortDirection}
					territories={territories}
					onAssociate={handleOpenSidePanel}
					onDelete={handleOpenDeleteDialog}
					onEdit={handleOpenSidePanel}
					onSort={handleSort}
				/>
			</div>
			<TablePagination
				component="div"
				count={totalElements}
				labelRowsPerPage={translate("common:pagination.row-per-page")}
				page={page}
				rowsPerPage={rowsPerPage}
				rowsPerPageOptions={rowsPerPageOptions}
				onPageChange={handleChangePage}
				onRowsPerPageChange={handleChangeRowsPerPage}
			/>
			<DistributionListSidePanel
				isEditing={!!selectedTerritory?.email}
				open={sidePanelOpen}
				territory={selectedTerritory}
				onClose={handleCloseSidePanel}
				onSave={handleSave}
			/>
			<CustomDialog
				iconColor="var(--red-500)"
				iconModel={icon.faExclamationTriangle}
				open={deleteDialogOpen}
				submitLabel={translate("common:btn.confirm")}
				subTitle={<b>{translate("admin.distribution-list.delete.confirm")}</b>}
				onClose={handleCloseDeleteDialog}
				onSubmit={handleConfirmDelete}
			/>
		</>
	);
}
